import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { AcceptInvite } from '@hotjar/webapp/domains/invitations/model';
import { acceptInvite as acceptInviteApi } from '@hotjar/webapp/domains/invitations/api/invitations';
import { redirectMfaSetup } from '@hotjar/auth';
import { refreshFromServer } from '@hotjar/webapp/domains/common/user-data';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { formSubmit } from '@hotjar/tracking';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import { useMFALogin } from '@hotjar/webapp/features/Mfa/useMFALogin';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';

interface AcceptInviteProps {
  name: string;
  password: string;
  role: string;
  terms: boolean;
}

export const useAcceptInvite = ({
  email,
  ssoToken,
  isGoogleSSOProvider,
}: {
  email: string;
  ssoToken: string;
  isGoogleSSOProvider: boolean;
}): { acceptInvite: (props: AcceptInviteProps) => Promise<void> } => {
  const tokenQueryParam = useQueryParameter('token');
  const nextParam = useQueryParameter('next');

  const { onLogin } = useMFALogin();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];

  const { success: notifySuccess, unhandledError } = useNotify();

  const acceptInvite = async ({
    name: userName,
    password,
    role,
    terms,
  }: AcceptInviteProps): Promise<void> => {
    const data: AcceptInvite = {
      email,
      name: userName,
      password,
      role,
      terms,
      token: tokenQueryParam || '',
      ssoProvider: isGoogleSSOProvider ? 'google' : null,
      ssoToken: ssoToken || null,
      subscribeToEmails: false,
      locale: currentLocale,
    };

    try {
      const { enforcedMfa } = await acceptInviteApi(data);
      if (enforcedMfa) {
        redirectMfaSetup(nextParam);
        return;
      }

      await refreshFromServer()
        .then(onLogin)
        .catch(() => {
          // Register worked, but login failed (maybe tried to register during a deploy?).
          notifySuccess(t('activation:onboarding.onboarding-success'));
          // Let's send them to the login screen.
          setTimeout(() => {
            navigate(LOGIN.path, { replace: true });
          }, 5000);
        });
    } catch (err) {
      formSubmit({
        name: 'User invite signup',
        submitSuccess: false,
        groupData: undefined,
        data: { app_form_submit_failure_reason: 'Unhandled Error' },
      });
      unhandledError();
      // NOTE: deferred.reject() is intentionally not done.
      // We don't resolve this promise as we don't want the user to resubmit this form.
    }
  };

  return {
    acceptInvite,
  };
};
